import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { storeUtils } from './utils';
import type { Quotation } from '../shared/schemas/quotation.schema';
import mockData from '../mocks/quotations.json';

interface QuotationState {
    quotations: Quotation[];
    isLoading: boolean;
    initialized: boolean;
    loadQuotations: () => Promise<void>;
    addQuotation: (data: Omit<Quotation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<boolean>;
    updateQuotation: (id: string, data: Partial<Quotation>) => Promise<boolean>;
    deleteQuotation: (id: string) => Promise<boolean>;
}

let idCounter = 100;
const generateId = (prefix: string) => `${prefix}_${String(++idCounter).padStart(3, '0')}`;

export const useQuotationStore = create<QuotationState>()(
    persist(
        (set, get) => ({
            quotations: [],
            isLoading: false,
            initialized: false,

            loadQuotations: async () => {
                if (get().initialized && get().quotations.length > 0) return;
                set({ isLoading: true });
                await new Promise((r) => setTimeout(r, 300));
                set({ quotations: mockData as unknown as Quotation[], isLoading: false, initialized: true });
            },

            addQuotation: async (data) => {
                try {
                    await new Promise((r) => setTimeout(r, 200));
                    const now = new Date().toISOString();
                    const newItem: Quotation = { ...data, id: generateId('qt'), createdAt: now, updatedAt: now };
                    set((s) => ({ quotations: storeUtils.append(s.quotations, newItem) }));
                    return true;
                } catch { return false; }
            },

            updateQuotation: async (id, data) => {
                try {
                    await new Promise((r) => setTimeout(r, 200));
                    const now = new Date().toISOString();
                    set((s) => ({ quotations: storeUtils.map(s.quotations, (q) => q.id !== id ? q : { ...q, ...data, updatedAt: now } as Quotation) }));
                    return true;
                } catch { return false; }
            },

            deleteQuotation: async (id) => {
                try {
                    await new Promise((r) => setTimeout(r, 200));
                    set((s) => ({ quotations: storeUtils.filter(s.quotations, (q) => q.id !== id) }));
                    return true;
                } catch { return false; }
            },
        }),
        { name: 'quotation-storage', partialize: (state) => ({ quotations: state.quotations, initialized: state.initialized }) },
    ),
);
